'use client';

import { HStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { ArrowDown01Icon, Refresh04Icon } from '@terra/ui-icons';
import { Button } from '../../../components/button';
import { Menu } from '../../../components/menu';
import { Tooltip } from '../../../components/tooltip';

type UnverifiedAssetRerunControlsProps = {
  onRerun?: () => void;
  onRerunWithContext?: () => void;
  isRerunning?: boolean;
};

export function UnverifiedAssetRerunControls({
  onRerun,
  onRerunWithContext,
  isRerunning = false,
}: UnverifiedAssetRerunControlsProps) {
  const t = useTranslations('designSystem.applicationAssets');
  const rerunLabel = t('actions.rerun');
  const rerunWithContextLabel = t('actions.rerunWithContext');
  const rerunUnavailableLabel = t('actions.rerunUnavailable');
  const canRerun = Boolean(onRerun) && !isRerunning;
  const canOpenContextMenu = Boolean(onRerunWithContext) && !isRerunning;

  return (
    <HStack gap={0}>
      <Tooltip title={onRerun ? rerunLabel : rerunUnavailableLabel}>
        <Button
          variant="outline"
          size="xs"
          disabled={!canRerun}
          onClick={onRerun}
          aria-label={rerunLabel}
          loading={isRerunning}
          loadingText={rerunLabel}
          borderRightRadius={onRerunWithContext ? '0' : undefined}
          gap={2}
        >
          <Refresh04Icon size={14} />
          {rerunLabel}
        </Button>
      </Tooltip>
      {onRerunWithContext ? (
        <Menu.Root
          positioning={{
            strategy: 'fixed',
            placement: 'bottom-end',
          }}
        >
          <Menu.Trigger asChild>
            <Button
              variant="outline"
              size="xs"
              aria-label={rerunWithContextLabel}
              disabled={!canOpenContextMenu}
              px={2}
              borderLeftRadius="0"
            >
              <ArrowDown01Icon size={14} />
            </Button>
          </Menu.Trigger>
          <Menu.Positioner>
            <Menu.Content
              minW="200px"
              p="1"
              shadow="lg"
              borderWidth="0"
              bg="bg.canvas"
            >
              <Menu.Item
                value="rerun-with-context"
                onClick={onRerunWithContext}
                display="flex"
                alignItems="center"
                gap="2"
              >
                <Refresh04Icon size={14} />
                {rerunWithContextLabel}
              </Menu.Item>
            </Menu.Content>
          </Menu.Positioner>
        </Menu.Root>
      ) : null}
    </HStack>
  );
}
